import { MOTION_ORIGIN, REFERENCE_HEIGHT, REFERENCE_WIDTH, type HeroAsset } from './layout';

export type MotionOrigin = { x: number; y: number };
export type ReferenceSize = { width: number; height: number };
export const DEFAULT_MOTION_ORIGIN: MotionOrigin = MOTION_ORIGIN;

// ---------------------------------------------------------------------------
// Easings
// ---------------------------------------------------------------------------

export const easeOutCubic = (t: number): number => {
	if (t <= 0) return 0;
	if (t >= 1) return 1;
	return 1 - Math.pow(1 - t, 3);
};

export const clamp01 = (x: number): number => {
	return x < 0 ? 0 : x > 1 ? 1 : x;
};

// ---------------------------------------------------------------------------
// Seeded RNG (mulberry32) keyed by asset id, so reloads keep the same feel.
// ---------------------------------------------------------------------------

export const mulberry32 = (seed: number): (() => number) => {
	let s = seed >>> 0;
	return () => {
		s = (s + 0x6d2b79f5) >>> 0;
		let t = s;
		t = Math.imul(t ^ (t >>> 15), t | 1);
		t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
		return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
	};
};

/** FNV-1a 32-bit hash of an arbitrary string id. */
export const hashSeed = (input: string): number => {
	let h = 0x811c9dc5;
	for (let i = 0; i < input.length; i++) {
		h ^= input.charCodeAt(i);
		h = Math.imul(h, 0x01000193);
	}
	return h >>> 0;
};

// ---------------------------------------------------------------------------
// Per-asset motion parameters (computed once at module init)
// ---------------------------------------------------------------------------

export type AssetMotionParams = {
	id: string;
	/** Resting position, 0..1 normalized stage coords. */
	restX: number;
	restY: number;
	/** Depth -1..1; bigger = closer to camera. */
	z: number;
	/** 0..1 normalized depth, used for amplitude scaling. */
	normZ: number;
	/** Distance from origin as a 0..1 fraction of the max distance in the set. */
	distFactor: number;
	/** Stable per-asset random in 0..1 used for stagger jitter. */
	jitterFactor: number;
	/** Stable per-asset random in 0..1 used to vary per-item duration. */
	durationFactor: number;
	// Ambient drift params (sine waves with stable random freq/phase/amp).
	ampX: number;
	ampY: number;
	freqX: number;
	freqY: number;
	phaseX: number;
	phaseY: number;
};

export const computeAssetParams = (
	assets: readonly HeroAsset[],
	motionOrigin: MotionOrigin = DEFAULT_MOTION_ORIGIN,
): Map<string, AssetMotionParams> => {
	const widths = assets.map((a) => a.width);
	const minW = Math.min(...widths);
	const maxW = Math.max(...widths);
	const widthRange = Math.max(maxW - minW, 1e-6);

	// Bigger icons read as closer (positive z), smaller as farther (negative z).
	const zOf = (a: HeroAsset): number =>
		a.z !== undefined ? a.z : ((a.width - minW) / widthRange) * 2 - 1;

	const dists = assets.map((a) => Math.hypot(a.x - motionOrigin.x, a.y - motionOrigin.y));
	const maxDist = Math.max(...dists, 1e-6);

	const out = new Map<string, AssetMotionParams>();
	for (let i = 0; i < assets.length; i++) {
		const asset = assets[i];
		const rnd = mulberry32(hashSeed(asset.id));
		const z = zOf(asset);
		const normZ = (z + 1) / 2;
		// Closer items get larger ambient amplitude (sells depth without true 3D).
		const ampScale = 0.4 + 1.0 * normZ;
		out.set(asset.id, {
			id: asset.id,
			restX: asset.x,
			restY: asset.y,
			z,
			normZ,
			distFactor: dists[i] / maxDist,
			jitterFactor: rnd(),
			durationFactor: rnd(),
			ampX: (2 + rnd() * 4) * ampScale,
			ampY: (2 + rnd() * 4) * ampScale,
			freqX: 0.00035 + rnd() * 0.00025,
			freqY: 0.00035 + rnd() * 0.00025,
			phaseX: rnd() * Math.PI * 2,
			phaseY: rnd() * Math.PI * 2,
		});
	}
	return out;
};

export type StageRect = { width: number; height: number };

// ---------------------------------------------------------------------------
// Layer 2 — Ambient drift
// ---------------------------------------------------------------------------

export type AmbientResult = { dx: number; dy: number };

export const computeAmbient = (
	nowMs: number,
	params: AssetMotionParams,
	ampMultiplier: number,
): AmbientResult => {
	if (ampMultiplier <= 0) return { dx: 0, dy: 0 };
	return {
		dx: Math.sin(nowMs * params.freqX + params.phaseX) * params.ampX * ampMultiplier,
		dy: Math.cos(nowMs * params.freqY + params.phaseY) * params.ampY * ampMultiplier,
	};
};

// ---------------------------------------------------------------------------
// Coordinate conversion helper for SVG edge endpoints.
// ---------------------------------------------------------------------------

/**
 * Convert a container-pixel offset to an offset in viewBox units so it
 * can be applied to an SVG line `x1/y1/x2/y2` that lives in a viewBox of
 * `refWidth × refHeight`.
 *
 * `box` is the bounding rect of the **container the SVG fills** (the icon
 * layer in this component, not the outer stage). `refWidth` / `refHeight`
 * default to the desktop reference frame; the hook overrides them with
 * the SVG's actual `viewBox.baseVal` so mobile (650 × 1200) and desktop
 * (1680 × 1200) both convert correctly.
 *
 * The SVG uses `preserveAspectRatio="none"`, so the viewBox is stretched
 * to fit the container independently on each axis — that's why X and Y
 * offsets must be scaled using their respective container dimensions.
 */
export const pxToViewBox = (
	dxPx: number,
	dyPx: number,
	box: StageRect,
	referenceSize: ReferenceSize = { width: REFERENCE_WIDTH, height: REFERENCE_HEIGHT },
): { dx: number; dy: number } => {
	return {
		dx: box.width > 0 ? (dxPx / box.width) * referenceSize.width : 0,
		dy: box.height > 0 ? (dyPx / box.height) * referenceSize.height : 0,
	};
};
